<template>
  <div class="product">
    <div class="product__inner">
      <img class="product__image" :src="product.images.edges[0].node.image" :alt="product.name">
      <div class="product__hover">
        <div class="product__hover-block" v-if="isProductInCart">
          <button class="product__hover-button" @click="removeAllProductFormCart">{{ t('buttons.removeProducts') }}</button>
          <div class="product__hover-manage">
            <div class="product__hover-manage-button" @click="removeProductFormCart">-</div>
            <div class="product__hover-manage-count">{{ productQuantity }}</div>
            <div class="product__hover-manage-button" @click="addToCart">+</div>
          </div>
        </div>
        <button class="product__hover-button" @click="addToCart" v-else>{{ user ? t('buttons.add') : t('buttons.login') }}</button>
        <router-link :to="translations.i18nRoute({ name: 'store', query: { productUuid: product.uuid } })">
          <svg viewBox="0 0 24 24" width="60" class="product__hover-eye" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="SVGRepo_iconCarrier">
            <g clip-path="url(#clip0_15_200)">
              <circle cx="12" cy="13" r="3" fill="currentColor" stroke-linejoin="round"></circle>
              <path d="M12 7.5C7.69517 7.5 4.47617 11.0833 3.39473 12.4653C3.14595 12.7832 3.14595 13.2168 3.39473 13.5347C4.47617 14.9167 7.69517 18.5 12 18.5C16.3048 18.5 19.5238 14.9167 20.6053 13.5347C20.8541 13.2168 20.8541 12.7832 20.6053 12.4653C19.5238 11.0833 16.3048 7.5 12 7.5Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
            </g>
          </g>
        </svg>
        </router-link>
      </div>
    </div>
    <p class="product__name"> {{ product.name }}</p>
  </div>
</template>

<script setup>
import {computed} from "vue";
import {useAuthStore} from "@/stores/auth.js";
import {useI18n} from "vue-i18n";
import {useAppStore} from "@/stores/app.js";
import translations from "@/core/helpers/translations.js";
import {useCartStore} from "@/stores/cart.js";
import {Orders} from "@/core/api/orders.js";
import {ElNotification} from "element-plus";

const authStore = useAuthStore()
const appStore = useAppStore()
const cartStore = useCartStore()
const {t} = useI18n()

const props = defineProps({
  product: Object
})

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder.uuid ? cartStore.currentOrder.uuid : null
})
const user = computed(() => {
  return authStore.user ? authStore.user : null
})

const isProductInCart = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === props.product.uuid)
})
const productQuantity = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.filter((prod) => prod.node.product.uuid === props.product.uuid)[0]?.node.quantity
})

const addToCart = async () => {
  if (user.value) {
    if (isProductInCart.value) {
      if (props.product.quantity > productQuantity.value) {
        const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);
        cartStore.setCurrentOrders(res.data.addOrderProduct.order)

        if (res.errors) {
          ElNotification({
            message: res.errors[0].message,
            type: "error"
          });
        } else {
          ElNotification({
            message: t('popup.addToCart', { product: props.product.name }),
            type: "success"
          });
        }
      } else {
        ElNotification({
          message: t('popup.addToCartLimit', { quantity: props.product.quantity }),
          type: "error"
        });
      }
    } else {
      const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);
      cartStore.setCurrentOrders(res.data.addOrderProduct.order)

      if (res.errors) {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      } else {
        ElNotification({
          message: t('popup.addToCart', { product: props.product.name }),
          type: "success"
        });
      }
    }
  } else {
    appStore.setActiveState('signIn')
  }
}

const removeProductFormCart = async () => {
  if (productQuantity.value === 1) {
    await removeAllProductFormCart()
    return
  }

  const res = await Orders.overwriteCart("removeOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);

  cartStore.setCurrentOrders(res.data.removeOrderProduct.order)
}

const removeAllProductFormCart = async () => {
  const res = await Orders.overwriteCart("removeOrderProductsOfAKind", accessToken.value, orderUuid.value, props.product.uuid);
  cartStore.setCurrentOrders(res.data.removeOrderProductsOfAKind.order)
}
</script>

<style lang="scss" scoped>
.product {
  width: 362px;
  background-color: rgba(#898989, 0.1);
  padding: 25px;
  border-radius: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  
  &__inner {
    width: 100%;
    height: 360px;
    position: relative;

    @include hover {
      .product__hover {
        opacity: 1;
      }
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
  }

  &__name {
    word-break: break-all;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    font-family: Poppins, sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: $black;
  }
  
  &__hover {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-color: rgba(33, 33, 33, 0.6);
    backdrop-filter: blur(4px);
    opacity: 0;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 15px;
    transition: 0.2s;

    &-button {
      cursor: pointer;
      border: 1px solid $white;
      border-radius: 4px;
      padding: 5px 20px;
      background-color: transparent;
      transition: 0.2s;

      color: $white;
      font-family: Poppins, sans-serif;
      font-size: 28px;
      font-weight: 600;

      @include hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    &-eye {
      cursor: pointer;
      transition: 0.2s;
      color: #bababa;

      @include hover {
        color: $white;
      }
    }

    &-block {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 15px;
    }

    &-manage {
      display: grid;
      grid-template-columns: 1fr 2fr 1fr;
      border-radius: 4px;
      height: 35px;

      & div {
        display: grid;
        place-items: center;

        font-size: 28px;
      }

      &-button {
        cursor: pointer;
        background-color: $main;
        border-radius: 4px 0 0 4px;
        transition: 0.2s;

        color: $white;

        @include hover {
          background-color: $mainHover;
        }

        &:last-child {
          border-radius: 0 4px 4px 0;
        }
      }

      &-count {
        background-color: $white;

        font-size: 22px !important;
      }
    }
  }
}

@media (max-width: 1300px) {
  .product {
    width: 300px;

    &__inner {
      height: 320px;
    }
  }
}
</style>